import { useState, useEffect, useRef } from 'react';
import AdvanceDateRangePicker from '../AdvanceDateRangePicker';
import BarGraph from './BarGraph';
import moment from 'moment';
import { makeRESTApiRequest, ABORT_ERROR } from '../../../helpers/api';
import { useAdvancedDateRangePicker } from '../../../hooks/useAdvancedDateRangePicker';
import { BsFillBarChartFill } from 'react-icons/bs';
import BarChartLoader from '../../BarChartLoader';

export default function StatsBarCard({
  endpoint,
  title,
  isStacked = false,
  barTitle,
  showTotalPercentage = true,
  icon,
  headerBg,
  iconBg,
  organizationIds,
  globalDateRange
}) {
  const { dateRange, setDateRange, handleNavigateDate } = useAdvancedDateRangePicker(globalDateRange);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (globalDateRange) {
      setDateRange(globalDateRange);
    }
  }, [globalDateRange]);

  useEffect(() => {
    const fetchStats = async () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      setLoading(true);

      // Create new AbortController for this request
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        // Ensure startDate is at start of day and endDate is at end of day
        const startDate = dateRange.startDate ? moment(dateRange.startDate).startOf('day') : null;
        const endDate = dateRange.endDate ? moment(dateRange.endDate).endOf('day') : null;

        const { response, error } = await makeRESTApiRequest({
          endpoint,
          method: 'POST',
          requestBody: {
            organizationIds: organizationIds,
            dateFilter: {
              startDate: startDate?.toISOString() || null,
              endDate: endDate?.toISOString() || null
            }
          },
          signal: controller.signal
        });

        if (!controller.signal.aborted) {
          if (error && error !== ABORT_ERROR) {
            setStats(null);
          } else if (response && response.data) {
            setStats(response.data);
          } else if (response) {
            setStats(response);
          }
          setLoading(false);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error('Error fetching stats:', error);
          setStats(null);
          setLoading(false);
        }
      }
    };

    fetchStats();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [dateRange, endpoint, organizationIds.length]);

  return (
    <div className="mb-4 h-100">
      <div className="card border shadow-sm h-100 rounded" style={{ minHeight: 380 }}>
        {/* Header with icon, title, and date picker */}
        <div
          className="d-flex flex-wrap justify-content-between align-items-center mb-0 p-2 border-bottom"
          style={{ borderTopLeftRadius: '0.5rem', borderTopRightRadius: '0.5rem', background: headerBg || '#f8f9fa' }}
        >
          <div className="d-flex align-items-center gap-2 ">
            {icon && (
              <span
                className="d-flex align-items-center justify-content-center "
                style={{ width: 24, height: 24, borderRadius: '50%', background: iconBg, marginRight: '10px' }}
              >
                {icon}
              </span>
            )}
            <h4 className="fw-bold mb-0" style={{ color: '#212529', minWidth: 180, fontSize: '1rem' }}>
              {title}
            </h4>
          </div>
          <div className="d-flex align-items-center gap-2">
            <AdvanceDateRangePicker
              value={dateRange}
              onChange={ranges => setDateRange(ranges.selection)}
              className=""
              showNavigator={true}
              rangeType={dateRange.rangeType}
              handleNavigateDate={handleNavigateDate}
            />
          </div>
        </div>
        <div className="card-body h-100 d-flex flex-column justify-content-center" style={{ minHeight: 280 }}>
          {loading ? (
            <div className="d-flex align-items-center justify-content-start h-100 w-100">
              <BarChartLoader />
            </div>
          ) : stats ? (
            <BarGraph
              chartData={stats.chartData}
              countByOrderType={stats?.countByOrderType || null}
              interval={!dateRange.startDate && !dateRange.endDate ? 'yearly' : stats.interval || 'monthly'}
              isStacked={isStacked}
              barTitle={barTitle}
              themeColor={headerBg}
              showTotalPercentage={showTotalPercentage}
            />
          ) : (
            <div className="d-flex flex-column align-items-center justify-content-center text-center text-muted h-100 w-100">
              <BsFillBarChartFill size={48} color="#bdbdbd" />
              <p className="mt-3">{'No chart data available'}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
